import React, { useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Table, Spinner, Alert, Input, Button,
  FormGroup, Label, Nav, NavItem, NavLink, Badge,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../Request";
import { REPORTING_URL } from "../../api";

// Loan Statement (SOA) + Loan Card — document-style outputs for one loan, searched by
// Customer ID (ML…/id) or Loan Code (LI…), exportable as PDF or Excel/CSV.

const cardStyle = { border: "1px solid #e9ecef", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" };

const inr = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const two = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (isNaN(n)) return v;
  return Number.isInteger(n) ? n : Math.round(n * 100) / 100;
};
const dt = (v) => (v ? String(v).slice(0, 10) : "-");
const EMI_STATUS = { 1: "Pending", 2: "Part-paid", 3: "Overdue", 4: "Paid", 5: "Waived" };

const STMT_COLS = [
  ["emi_number", "EMI #"], ["emi_date", "Due Date"], ["emi_amount", "EMI Amount"],
  ["principal_due", "Principal Due"], ["interest_due", "Interest Due"],
  ["outstanding_amount", "Outstanding"], ["dpd", "DPD"], ["emi_status", "Status"],
];
const LEDGER_COLS = [
  ["date", "Date"], ["description", "Description"], ["mode", "Mode"],
  ["instrument_no", "Instrument / UTR"], ["debit", "Debit"], ["credit", "Credit"], ["balance", "Balance"],
];

const LoanDocuments = () => {
  const [mode, setMode] = useState("statement"); // statement | card
  const [customerId, setCustomerId] = useState("");
  const [loanCode, setLoanCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDoc = async (overrideLoan) => {
    setLoading(true); setError(""); setData(null);
    try {
      const url = mode === "card" ? REPORTING_URL.MERCHANT_LOAN_CARD : REPORTING_URL.MERCHANT_LOAN_STATEMENT;
      const params = {};
      if (overrideLoan) params.loanCode = overrideLoan;
      else {
        if (customerId.trim()) params.customerId = customerId.trim();
        if (loanCode.trim()) params.loanCode = loanCode.trim();
      }
      if (!params.customerId && !params.loanCode) {
        setError("Enter a Customer ID (ML…/id) or a Loan Code (LI…).");
        return;
      }
      const res = await axiosInstance.get(url, { params });
      if (!res.data || !res.data.loan_id) {
        setError("No loan found for the given Customer ID / Loan Code.");
        return;
      }
      setData(res.data);
    } catch (e) {
      setError(e?.response?.status === 401
        ? "Session expired or not authorized. Please log in again."
        : (e?.response?.data?.message || e.message || "Failed to load."));
    } finally {
      setLoading(false);
    }
  };

  // ---- exports ----------------------------------------------------------
  const baseName = () => `${mode === "card" ? "loan_card" : "loan_statement"}_${data?.loan_id || "doc"}`;

  const headerPairs = () => {
    if (!data) return [];
    const common = [
      ["Customer ID", data.customer_id], ["Loan ID", data.loan_id], ["Lead ID", data.lead_id],
      ["Fintech", data.fintech_name], ["Borrower", data.borrower_name], ["Mobile", data.mobile],
      ["PAN", data.pan], ["Disbursed Date", dt(data.disbursed_date)],
      ["Disbursed Amount", inr(data.disbursed_amount)], ["Loan Amount", inr(data.loan_amount)],
      ["Tenure", data.tenure], ["EMI Amount", inr(data.emi_amount)],
      ["ROI / day", data.roi_per_day], ["APR", data.apr],
      ["Processing Fee", inr(data.processing_fee)], ["GST", inr(data.gst)],
      ["Status", data.status], ["Close Date", dt(data.close_date)],
    ];
    if (mode === "statement") return [["Address", data.address], ...common];
    return [...common,
      ["Total Paid", inr(data.total_paid)], ["Principal Paid", inr(data.principal_paid)],
      ["Interest Paid", inr(data.interest_paid)], ["Penal Paid", inr(data.penal_paid)],
      ["Outstanding Principal", inr(data.os_principal)], ["Outstanding Interest", inr(data.os_interest)],
      ["Outstanding Penal", inr(data.os_penal)],
      ["EMIs Paid", `${data.emis_paid ?? 0} / ${data.total_emis ?? 0}`],
      ["Next Due Date", dt(data.next_due_date)], ["Worst DPD", data.worst_dpd ?? 0],
    ];
  };

  const downloadPdf = () => {
    const doc = new jsPDF();
    const title = mode === "card" ? "Loan Card" : "Loan Statement (Statement of Account)";
    doc.setFontSize(14); doc.text(title, 14, 16);
    doc.setFontSize(9);
    autoTable(doc, {
      startY: 22,
      head: [["Field", "Value"]],
      body: headerPairs().map(([k, v]) => [k, v == null ? "-" : String(v)]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [45, 85, 170] },
    });
    if (mode === "statement") {
      doc.setFontSize(11); doc.text("Installments", 14, doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 13,
        head: [STMT_COLS.map(([, l]) => l)],
        body: (data.installments || []).map((r) => STMT_COLS.map(([k]) =>
          k === "emi_status" ? (EMI_STATUS[r[k]] || r[k] || "-")
            : (k === "emi_date" ? dt(r[k]) : (r[k] ?? "-")))),
        styles: { fontSize: 7 }, headStyles: { fillColor: [45, 85, 170] },
      });
      doc.setFontSize(11); doc.text("Ledger", 14, doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 13,
        head: [LEDGER_COLS.map(([, l]) => l)],
        body: (data.ledger || []).map((r) => LEDGER_COLS.map(([k]) =>
          k === "date" ? dt(r[k]) : (r[k] ?? "-"))),
        styles: { fontSize: 7 }, headStyles: { fillColor: [45, 85, 170] },
      });
    }
    doc.save(`${baseName()}.pdf`);
  };

  const downloadExcel = (asCsv) => {
    const wb = XLSX.utils.book_new();
    const summaryAoa = [["Field", "Value"], ...headerPairs().map(([k, v]) => [k, v == null ? "" : String(v)])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryAoa), "Summary");
    if (mode === "statement") {
      const instAoa = [STMT_COLS.map(([, l]) => l), ...(data.installments || []).map((r) =>
        STMT_COLS.map(([k]) => k === "emi_status" ? (EMI_STATUS[r[k]] || r[k] || "")
          : (k === "emi_date" ? dt(r[k]) : two(r[k]))))];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(instAoa), "Installments");
      const ledAoa = [LEDGER_COLS.map(([, l]) => l), ...(data.ledger || []).map((r) =>
        LEDGER_COLS.map(([k]) => k === "date" ? dt(r[k]) : (["debit", "credit", "balance"].includes(k) ? two(r[k]) : (r[k] ?? ""))))];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ledAoa), "Ledger");
    }
    if (asCsv) {
      const sheet = wb.Sheets[wb.SheetNames[mode === "statement" ? 1 : 0]];
      const csv = XLSX.utils.sheet_to_csv(sheet);
      saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), `${baseName()}.csv`);
    } else {
      XLSX.writeFile(wb, `${baseName()}.xlsx`);
    }
  };

  // ---- render -----------------------------------------------------------
  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7 mb-4">
        <Container fluid>
          <h1 className="text-white mb-0">Loan Statement &amp; Card</h1>
          <p className="text-white mb-0" style={{ opacity: 0.85 }}>
            Statement of Account (SOA) and Loan Card for a single loan — search by Customer ID or Loan Code.
          </p>
        </Container>
      </div>

      <Container fluid className="mt--6">
        <Card style={{ ...cardStyle, marginBottom: "1.5rem" }}>
          <CardBody>
            <Nav pills className="mb-3">
              <NavItem>
                <NavLink href="#" active={mode === "statement"}
                  onClick={(e) => { e.preventDefault(); setMode("statement"); setData(null); }}>
                  Loan Statement
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink href="#" active={mode === "card"}
                  onClick={(e) => { e.preventDefault(); setMode("card"); setData(null); }}>
                  Loan Card
                </NavLink>
              </NavItem>
            </Nav>
            <Row className="align-items-end">
              <Col md="4">
                <FormGroup>
                  <Label className="fw-bold">Customer ID <span className="text-muted">(ML… or id)</span></Label>
                  <Input value={customerId} placeholder="Eg: ML26270009"
                    onChange={(e) => setCustomerId(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label className="fw-bold">Loan Code <span className="text-muted">(LI… — optional)</span></Label>
                  <Input value={loanCode} placeholder="Eg: LI…"
                    onChange={(e) => setLoanCode(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Button color="primary" onClick={() => fetchDoc()} disabled={loading}>
                    {loading ? <Spinner size="sm" /> : "View"}
                  </Button>
                </FormGroup>
              </Col>
            </Row>
            <small className="text-muted">
              Search by Customer ID returns that customer's latest loan; use the loan switcher (statement) or a Loan Code for a specific loan.
            </small>
          </CardBody>
        </Card>

        {error ? <Alert color="danger">{error}</Alert> : null}

        {data ? (
          <Card style={cardStyle}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                <h3 className="mb-0">
                  {mode === "card" ? "Loan Card" : "Statement of Account"} — {data.loan_id}
                </h3>
                <div className="d-flex" style={{ gap: 8 }}>
                  <Button size="sm" color="danger" outline onClick={downloadPdf}>
                    <i className="ni ni-single-copy-04 mr-1" /> PDF
                  </Button>
                  <Button size="sm" color="success" outline onClick={() => downloadExcel(false)}>Excel</Button>
                  <Button size="sm" color="secondary" outline onClick={() => downloadExcel(true)}>CSV</Button>
                </div>
              </div>

              {mode === "statement" && (data.customer_loans || []).length > 1 ? (
                <FormGroup className="mb-3" style={{ maxWidth: 320 }}>
                  <Label className="fw-bold">Switch loan (this customer)</Label>
                  <Input type="select" value={data.loan_id}
                    onChange={(e) => fetchDoc(e.target.value)}>
                    {data.customer_loans.map((l) => (
                      <option key={l.loan_code} value={l.loan_code}>{l.loan_code}</option>
                    ))}
                  </Input>
                </FormGroup>
              ) : null}

              <Row>
                {headerPairs().map(([k, v]) => (
                  <Col md="4" sm="6" key={k} className="mb-2">
                    <div style={{ fontSize: 11, color: "#8898aa", textTransform: "uppercase" }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{v == null || v === "" ? "-" : String(v)}</div>
                  </Col>
                ))}
              </Row>

              {mode === "statement" ? (
                <>
                  <h4 className="mt-4 mb-2">Installments</h4>
                  <div style={{ overflowX: "auto", maxHeight: 360 }}>
                    <Table bordered striped hover responsive size="sm" className="text-nowrap" style={{ fontSize: 12 }}>
                      <thead className="thead-light">
                        <tr>{STMT_COLS.map(([, l]) => <th key={l}>{l}</th>)}</tr>
                      </thead>
                      <tbody>
                        {(data.installments || []).map((r, i) => (
                          <tr key={i}>
                            {STMT_COLS.map(([k]) => (
                              <td key={k}>
                                {k === "emi_status" ? (
                                  <Badge color={r[k] === 4 ? "success" : r[k] === 3 ? "danger" : "secondary"}>
                                    {EMI_STATUS[r[k]] || r[k] || "-"}
                                  </Badge>
                                ) : k === "emi_date" ? dt(r[k])
                                  : ["emi_amount", "principal_due", "interest_due", "outstanding_amount"].includes(k)
                                    ? inr(r[k]) : (r[k] ?? "-")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <h4 className="mt-4 mb-2">Ledger</h4>
                  <div style={{ overflowX: "auto", maxHeight: 360 }}>
                    <Table bordered striped hover responsive size="sm" className="text-nowrap" style={{ fontSize: 12 }}>
                      <thead className="thead-light">
                        <tr>{LEDGER_COLS.map(([, l]) => <th key={l}>{l}</th>)}</tr>
                      </thead>
                      <tbody>
                        {(data.ledger || []).map((r, i) => (
                          <tr key={i}>
                            {LEDGER_COLS.map(([k]) => (
                              <td key={k}>
                                {k === "date" ? dt(r[k])
                                  : ["debit", "credit", "balance"].includes(k) ? inr(r[k]) : (r[k] ?? "-")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
      </Container>
    </>
  );
};

export default LoanDocuments;
